/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, effect, Input, model, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { AbstractFileStorageService, AttachmentUploadComponent } from '@well/file-storage';
import { CommonModule } from '@angular/common';
import { AttachmentListComponent } from '../attachment-list/attachment-list.component';
import { TranslateModule } from '@ngx-translate/core';
import { AttachmentVO } from '@well/attachment/domain';
import { CrmFileStorageService } from '@well/resource-services';

@Component({
  selector: 'well-attachment-uploader',
  templateUrl: './attachment-uploader.component.html',
  styleUrls: ['./attachment-uploader.component.scss'],
  standalone: true,
  imports: [CommonModule, AttachmentListComponent, TranslateModule, AttachmentUploadComponent],
  providers: [{ provide: AbstractFileStorageService, useClass: CrmFileStorageService }],
})
export class AttachmentUploaderComponent {
  @Input() header: string | undefined = undefined;
  @Input() browseLabel: string | undefined = undefined;
  @Input() attachments: AttachmentVO[] | undefined = undefined;
  @Input() canDelete = false;
  @Input() disabled = false;
  @Input() fileVersioning = false;
  @Input() maxFileSize = 10000000;
  @Input() accept: string | undefined = undefined;
  @Input() multiple = true;
  resetFiles = model(false);

  @Output() attachmentsSelect: Subject<AttachmentVO[]> = new Subject<AttachmentVO[]>();
  @Output() upload: Subject<File[]> = new Subject<File[]>();
  @Output() delete: Subject<string> = new Subject<string>();

  @ViewChild(AttachmentUploadComponent, { static: true }) attachmentUploader: AttachmentUploadComponent | undefined = undefined;

  constructor() {
    effect(() => {
      if(this.resetFiles()){
        this.attachments = [];
        this.attachmentsSelect.next([]);
        this.attachmentUploader?.clear();
      }
    });
  }

  onAttachmentsUploadCompleted(attachments: AttachmentVO[]) {
    this.attachmentsSelect.next(attachments);
    this.attachmentUploader?.clear();
  }

  onUpload(files: File[]) {
    this.upload.next(files);
  }

  uploadFiles(files?: File[]) {
    if (!files) {
      files = this.attachmentUploader?.localFiles;
    }

    if (this.fileVersioning) {
      const attachmentFileNames: string[] = this.attachments?.map((attachment) => attachment.fileName);
      const renamedFiles: File[] = [];
      files?.forEach((file) => {
        const versionedFileName: string = this.getVersionedFileName(file.name, null, attachmentFileNames);
        renamedFiles.push(new File([file], versionedFileName, { type: file.type }));
      });

      files = renamedFiles;
    }

    this.attachmentUploader?.uploadFiles(files);
  }

  private getVersionedFileName(
    baseFileName: string,
    versionedFileName: string,
    existingFileNames: string[],
    version = 1
  ) {
    if (!versionedFileName) versionedFileName = baseFileName;
    if (existingFileNames.includes(versionedFileName)) {
      const found: any[] = versionedFileName.match('\\(([0-9]+)\\)');
      if (!found) {
        versionedFileName += ' (' + version + ')';
      } else {
        const lastVersion: number = found[found.length - 1];
        const newVersion: number = +lastVersion + 1;
        versionedFileName = versionedFileName.replace(found[0], '(' + newVersion + ')');
      }
      return this.getVersionedFileName(baseFileName, versionedFileName, existingFileNames, version + 1);
    } else {
      return versionedFileName;
    }
  }
}
